import { MessageCircleOff } from "lucide-react";
import { ChatFileAttachment } from "./ChatFileAttachment";
import { ChatVoiceNote } from "./ChatVoiceNote";
import type { MessageDisplay } from "./ChatMessage";
import {
  CHAT_BODY_CLASS,
  renderChatBody,
} from "@/features/chat/utils/renderChatBody";

interface ChatMessageBubbleProps {
  message: MessageDisplay;
  /** Reply-preview click → scroll to the quoted message. */
  onJumpTo?: (messageId: string) => void;
}

export function ChatMessageBubble({ message, onJumpTo }: ChatMessageBubbleProps) {
  const isAudio =
    message.file?.type?.startsWith("audio") ||
    message.mediaUrl?.includes("audio");

  return (
    <div
      className={`flex flex-col gap-2 max-w-[480px] min-w-[200px] p-2 rounded-[12px] ${
        message.isSent
          ? "bg-[var(--color-green-1)] border border-[var(--color-green-3)] rounded-bl-[12px] rounded-br-[12px] rounded-tl-[12px] rounded-tr-[1px]"
          : "bg-white border border-[var(--color-black-1)] rounded-bl-[12px] rounded-br-[12px] rounded-tl-[1px] rounded-tr-[12px]"
      }`}
    >
      {/* Delete-for-everyone tombstone — content is cleared server-side */}
      {message.isDeleted && (
        <p className="flex items-center gap-1.5 text-[13px] italic text-[#9ba3a0]">
          <MessageCircleOff size={13} className="shrink-0" />
          {message.isSent
            ? "You deleted this message"
            : "This message was deleted"}
        </p>
      )}

      {/* Reply-to preview — click to jump to the quoted message */}
      {!message.isDeleted && message.replyTo && (
        <div
          role={onJumpTo ? "button" : undefined}
          tabIndex={onJumpTo ? 0 : undefined}
          title={onJumpTo ? "Go to the original message" : undefined}
          onClick={onJumpTo ? () => onJumpTo(message.replyTo!.id) : undefined}
          onKeyDown={
            onJumpTo
              ? (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onJumpTo(message.replyTo!.id);
                  }
                }
              : undefined
          }
          className={`flex items-start gap-1.5 px-2.5 py-1.5 bg-black/5 rounded-lg border-l-2 border-[#218760] ${
            onJumpTo ? "cursor-pointer hover:bg-black/10" : ""
          }`}
        >
          <div className="min-w-0">
            <p className="text-[10px] font-semibold text-[#218760]">
              {message.replyTo.sender?.name}
            </p>
            <p className="text-[10px] text-[#697571] truncate">
              {message.replyTo.type === "deleted"
                ? "This message was deleted"
                : message.replyTo.body || message.replyTo.type}
            </p>
          </div>
        </div>
      )}

      {/* Text — line breaks / indentation preserved, @mentions highlighted */}
      {message.text && (
        <p
          className={`text-[14px] font-normal text-[var(--color-black-8)] tracking-[-0.56px] leading-[1.45] ${CHAT_BODY_CLASS}`}
        >
          {renderChatBody(message.text, message.mentions)}
        </p>
      )}

      {/* Voice note — WhatsApp-style player */}
      {isAudio && message.mediaUrl && (
        <ChatVoiceNote
          src={message.mediaUrl}
          type={message.file?.type}
          isSent={message.isSent}
        />
      )}

      {/* Image */}
      {message.file?.type?.startsWith("image") && message.mediaUrl && (
        <img
          src={message.mediaUrl}
          alt={message.file.name}
          className="rounded-lg max-w-[300px] max-h-[200px] object-cover cursor-pointer"
          onClick={() => window.open(message.mediaUrl, "_blank")}
        />
      )}

      {/* Document attachment */}
      {message.file && !isAudio && !message.file.type?.startsWith("image") && (
        <ChatFileAttachment file={message.file} mediaUrl={message.mediaUrl} />
      )}

      {/* Footer: author + time. The author is printed whenever the mapper set
          one — for a received message as before, and ALSO on an own-account
          message that a manager sent while viewing as it ("Switch user"), so
          it is never mistaken for something the account holder wrote. */}
      <div
        className={`flex gap-2 items-center text-[11px] font-normal text-[var(--color-black-5)] tracking-[-0.22px] leading-normal ${
          message.isSent && !message.author ? "justify-end" : "justify-between"
        }`}
      >
        {message.author && (
          <p
            className="flex-1 min-w-0 truncate"
            title={
              message.authorVia
                ? `${message.author} wrote this while viewing as ${message.authorVia}`
                : undefined
            }
          >
            {message.author}
            {message.authorVia && (
              <span className="text-[var(--color-black-4)]">
                {" "}
                · as {message.authorVia}
              </span>
            )}
            {message.role && `(${message.role})`}
          </p>
        )}
        <p className={message.isSent && !message.author ? "" : "text-right flex-1"}>
          {message.isEdited && (
            <span className="italic text-[var(--color-black-5)] mr-1.5">
              edited
            </span>
          )}
          {message.time}
        </p>
      </div>

      {/* Read receipts */}
      {message.readBy && message.readBy.length > 0 && message.isSent && (
        <p className="text-[9px] text-[#9ba3a0] text-right">
          Seen by {message.readBy.length}
        </p>
      )}
    </div>
  );
}
